import { SDK_VERSION } from '@sentry/browser';
import {
  AnalyticsEvent,
  ButtonComponentV2,
  ProcessTypeInput,
  TabaPayChallengeAuthenticateInput,
  TabaPayChallengeAuthenticateResultTypenames,
  TabaPayChallengeInitInput,
  TabaPayChallengeLookupResultTypenames,
  Typenames,
} from '@varo/graphql-schema-types';
import useCancelTabapayTransfer from 'features/moveMoney/screens/dcf/hooks/useCancelTabapayTransfer';
import { useTabaPayChallengeAuthenticate } from 'features/moveMoney/screens/dcf/hooks/useTabaPayChallengeAuthenticate';
import { useTabaPayChallengeInit } from 'features/moveMoney/screens/dcf/hooks/useTabaPayChallengeInit';
import { useTabaPayChallengeLookup } from 'features/moveMoney/screens/dcf/hooks/useTabaPayChallengeLookup';
import {
  handle3dsActionCode,
  logDebitCardCheckFailedEvents,
  logDebitCardCheckPassedEvents,
  logDebitCardCheckStepCompletedEvents,
} from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import {
  getTabapayErrorScreen,
  getTabapayPartialAuthErrorScreen,
} from 'features/myVaro/screens/ExternalAccounts/helper/TabapayHelper';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isWeb } from 'shared/constants/platform';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { ErrorScreenType } from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { logError } from 'shared/utils/sentry/sentry';

import {
  cleanupCardinalDOM,
  cleanupCardinalListeners,
  injectCardinalScript,
  SessionData,
  ThreeDsValidationResponse,
} from './threeDsSdkWeb.helpers';

const useThreeDsSdkWeb = () => {
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'paymentDetails',
  });
  const { t: translatedTextMyVaro } = useTranslation('myVaro');
  const [isScriptInjected, setIsScriptInjected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [initTransactionId, setInitTransactionId] = useState<string>('');
  const [errorScreenThreeDs, setErrorScreen] = useState<{
    screenType: ErrorScreenType.Error;
    messageHeadline: string;
    messageBody: string;
    primaryButton?: ButtonComponentV2;
    secondaryButton?: ButtonComponentV2;
    is3DSError?: boolean;
    processType?: string;
    analytics?: AnalyticsEvent;
    isUserCancelled3DS?: boolean;
    isPartialAuth?: boolean;
    approvedAmount?: string;
    statusCode?: string;
  } | null>(null);
  const { cancelTabapayTransfer } = useCancelTabapayTransfer();
  const { triggerChallengeInit } = useTabaPayChallengeInit();
  const { triggerChallengeLookup } = useTabaPayChallengeLookup();
  const { triggerAuthenticate } = useTabaPayChallengeAuthenticate();
  const { segmentClient } = useAnalyticsConfiguration();

  const injectScript = useCallback(() => {
    injectCardinalScript(() => setIsScriptInjected(true));
  }, []);

  const resetThreeDsSession = useCallback(() => {
    cleanupCardinalListeners();
    setInitTransactionId('');
    setErrorScreen(null);
    setIsLoading(false);
  }, []);

  const handleAuthenticate = async (
    ds: string,
    jwt: string,
    transaction: string,
    processType: ProcessTypeInput,
    flowName: string,
  ) => {
    if (!ds || !jwt || !transaction) {
      throw new Error('Missing required parameters for Authentication');
    }
    const authenticateInput: TabaPayChallengeAuthenticateInput = {
      dsId: ds,
      jwtToken: jwt,
      transactionId: transaction,
      processType: processType,
    };
    const result = await triggerAuthenticate(authenticateInput);
    const errorScreen = getTabapayErrorScreen(result);
    if (errorScreen) {
      setIsLoading(false);
      setErrorScreen(errorScreen);
      logDebitCardCheckFailedEvents(
        flowName,
        errorScreen.code,
        errorScreen.messageBody,
        '3DS AUTH',
        segmentClient,
      );
    }

    const partialAuthErrorScreen = getTabapayPartialAuthErrorScreen(
      result,
      translatedTextMyVaro,
    );
    if (partialAuthErrorScreen) {
      setIsLoading(false);
      setErrorScreen(partialAuthErrorScreen);
    }

    if (
      result.__typename ===
      TabaPayChallengeAuthenticateResultTypenames.TabaPayChallengeAuthenticateResultError
    ) {
      setIsLoading(false);
      setErrorScreen({
        screenType: ErrorScreenType.Error,
        messageHeadline: result.title,
        messageBody: result.message,
      });
      logDebitCardCheckFailedEvents(
        flowName,
        result.code,
        result.message,
        '3DS AUTH',
        segmentClient,
      );
    }
    setIsLoading(false);

    if (
      result.__typename ===
      TabaPayChallengeAuthenticateResultTypenames.TabaPayChallengeAuthenticateResultSuccess
    ) {
      logDebitCardCheckPassedEvents('3DS AUTH', segmentClient, flowName);
    }
    return (
      result.__typename ===
      TabaPayChallengeAuthenticateResultTypenames.TabaPayChallengeAuthenticateResultSuccess
    );
  };

  const handleInit = async (
    processType: ProcessTypeInput,
    flowName: string,
    externalCardId?: string,
    transactionId?: string,
  ) => {
    setErrorScreen(null);
    let cardIdentifier: Partial<TabaPayChallengeInitInput> = {};

    if (externalCardId) {
      cardIdentifier = { externalCardID: `${externalCardId}` };
    } else if (transactionId) {
      cardIdentifier = { transactionId: `${transactionId}` };
    }

    const input: TabaPayChallengeInitInput = {
      sdkVersion: SDK_VERSION,
      processType: processType,
      ...cardIdentifier,
    };

    const result = await triggerChallengeInit(input);
    const errorScreen = getTabapayErrorScreen(result);
    if (errorScreen) {
      setIsLoading(false);
      setErrorScreen(errorScreen);
      logDebitCardCheckFailedEvents(
        flowName,
        errorScreen.code,
        errorScreen.messageBody,
        '3DS INIT',
        segmentClient,
      );
    }

    if (result.__typename === Typenames.TabaPayChallengeInitResultError) {
      logDebitCardCheckFailedEvents(
        flowName,
        result.code,
        result.message,
        '3DS INIT',
        segmentClient,
      );
      setErrorScreen({
        screenType: ErrorScreenType.Error,
        messageHeadline: result.title,
        messageBody: result.message,
      });
      setIsLoading(false);
    }

    if (result.__typename === Typenames.TabaPayChallengeInitResultSuccess) {
      logDebitCardCheckPassedEvents('3DS INIT', segmentClient, flowName);
      const isChallenge = result.challengeRequired;
      const { jwtToken, dsId, transactionID } = result;
      setInitTransactionId(transactionID);
      return { jwtToken, dsId, transactionID, isChallenge };
    }

    throw new Error('3DS Init failed');
  };

  const startThreeDsWebFlow = (
    processType: ProcessTypeInput,
    flowName: string,
    externalCardId?: string,
    sourceTransactionId?: string,
  ): Promise<boolean> => {
    return new Promise((resolve) => {
      const runFlow = async () => {
        try {
          if (!isWeb || typeof Cardinal === 'undefined') {
            throw new Error('Cardinal SDK not available');
          }
          setIsLoading(true);

          cleanupCardinalListeners();
          setInitTransactionId('');
          setErrorScreen(null);

          const { jwtToken, dsId, transactionID, isChallenge } =
            await handleInit(
              processType,
              flowName,
              externalCardId,
              sourceTransactionId,
            );

          if (!isChallenge) {
            setIsLoading(false);
            resolve(true);
            return;
          }

          Cardinal.setup('init', {
            jwt: jwtToken,
            display: { challengeWindowSize: '05' },
          });
          logDebitCardCheckStepCompletedEvents(
            '3ds_sdk_initiated',
            flowName,
            segmentClient,
          );

          logDebitCardCheckStepCompletedEvents(
            'sdk_setup_complete',
            flowName,
            segmentClient,
          );

          Cardinal.on('payments.setupComplete', async (rawData: any) => {
            const data = rawData as SessionData;
            try {
              logDebitCardCheckStepCompletedEvents(
                'cardinal_setup_completed',
                flowName,
                segmentClient,
              );
              const lookupResult = await triggerChallengeLookup({
                dsId,
                sdkSessionID: data.sessionId,
                isBrowser: true,
                transactionId: transactionID,
                processType: processType,
              });
              const errorScreen = getTabapayErrorScreen(lookupResult);
              if (errorScreen) {
                setIsLoading(false);
                setErrorScreen(errorScreen);
                logDebitCardCheckFailedEvents(
                  flowName,
                  errorScreen.code,
                  errorScreen.messageBody,
                  '3DS LOOKUP',
                  segmentClient,
                );
              }

              const partialAuthErrorScreen = getTabapayPartialAuthErrorScreen(
                lookupResult,
                translatedTextMyVaro,
              );

              if (partialAuthErrorScreen) {
                setIsLoading(false);
                setErrorScreen(partialAuthErrorScreen);
              }

              if (
                lookupResult.__typename ===
                TabaPayChallengeLookupResultTypenames.TabaPayChallengeLookupResultError
              ) {
                setErrorScreen({
                  screenType: ErrorScreenType.Error,
                  messageHeadline: lookupResult.title,
                  messageBody: lookupResult.message,
                });
                setIsLoading(false);
                logDebitCardCheckFailedEvents(
                  flowName,
                  lookupResult.code,
                  lookupResult.message,
                  '3DS LOOKUP',
                  segmentClient,
                );
              }

              if (
                lookupResult.__typename ===
                  TabaPayChallengeLookupResultTypenames.TabaPayChallengeLookupResultSuccess &&
                !lookupResult.payload
              ) {
                logDebitCardCheckPassedEvents(
                  '3DS LOOKUP',
                  segmentClient,
                  flowName,
                );
                setIsLoading(false);
                resolve(true);
                return;
              }

              if (
                lookupResult.__typename ===
                  TabaPayChallengeLookupResultTypenames.TabaPayChallengeLookupResultSuccess &&
                lookupResult.payload &&
                lookupResult.challengeURL &&
                lookupResult.processorTransactionId
              ) {
                logDebitCardCheckStepCompletedEvents(
                  'challenge_start',
                  flowName,
                  segmentClient,
                  '3DS LOOKUP',
                );
                logDebitCardCheckPassedEvents(
                  '3DS LOOKUP',
                  segmentClient,
                  flowName,
                );
                Cardinal.continue(
                  'cca',
                  {
                    AcsUrl: lookupResult.challengeURL,
                    Payload: lookupResult.payload,
                  },
                  {
                    OrderDetails: {
                      TransactionId: lookupResult.processorTransactionId,
                    },
                  },
                );
              } else {
                throw new Error('Lookup failed');
              }
            } catch (error) {
              logError(error);
              resolve(false);
            }
          });

          Cardinal.on(
            'payments.validated',
            async (rawData: any, jwt: string) => {
              const data = rawData as ThreeDsValidationResponse;

              logDebitCardCheckStepCompletedEvents(
                'challenge_validated',
                flowName,
                segmentClient,
              );

              cleanupCardinalListeners();

              if (
                processType !== ProcessTypeInput.Transaction &&
                data.ActionCode === 'FAILURE'
              ) {
                void cancelTabapayTransfer(
                  {
                    transactionId: transactionID,
                  },
                  flowName,
                );
              }
              try {
                await handle3dsActionCode({
                  actionCode: data.ActionCode,
                  setErrorScreen,
                  processType,
                  flowName,
                  translatedText,
                  segmentClient,
                  errorDescription: data.ErrorDescription,
                  errorNumber: data.ErrorNumber,
                  onSuccess: async () => {
                    const result = await handleAuthenticate(
                      dsId,
                      jwt,
                      transactionID,
                      processType,
                      flowName,
                    );
                    logDebitCardCheckStepCompletedEvents(
                      '3ds_authenticated',
                      flowName,
                      segmentClient,
                    );
                    logDebitCardCheckPassedEvents('', segmentClient, flowName);
                    resolve(result === true);
                  },
                  setIsLoading,
                  resolve: (response) => {
                    if (!response) {
                      resolve(false);
                      setIsLoading(false);
                    }
                  },
                });
              } catch (error) {
                logError(error);
                resolve(false);
              }
            },
          );
        } catch (error) {
          logError(error);
          resolve(false);
        }
      };

      void runFlow();
    });
  };

  const resetThreeDsSdk = useCallback((shouldUpdateState = true) => {
    if (isWeb) {
      cleanupCardinalDOM();
      if (shouldUpdateState) {
        setIsScriptInjected(false);
        setInitTransactionId('');
      }
    }
  }, []);

  useEffect(() => {
    if (isWeb) {
      injectScript();
    }
    return () => {
      resetThreeDsSdk(false);
    };
  }, [errorScreenThreeDs, injectScript, resetThreeDsSdk]);

  return {
    isReady: isScriptInjected && typeof Cardinal !== 'undefined',
    startThreeDsWebFlow,
    isLoading,
    errorScreenThreeDs,
    initTransactionId,
    setErrorScreen,
    resetThreeDsSession,
  };
};

export default useThreeDsSdkWeb;
